import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaEdit,
  FaFileImage,
  FaInfoCircle,
  FaPlus,
  FaRedoAlt,
  FaSearch,
  FaSave,
  FaTimes,
  FaUsers,
  FaWallet,
} from "react-icons/fa";
import api from "../lib/api";
import "./CSS/EmployeesPremium.css";

const BACKEND_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.fuelflow.in";

const salaryDayOptions = [...Array(31)].map((_, i) => {
  const d = i + 1;
  const suffix = d === 1 ? "st" : d === 2 ? "nd" : d === 3 ? "rd" : "th";
  return `${d}${suffix}`;
});

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "error" || notice.type === "warning"
      ? FaExclamationCircle
      : FaInfoCircle;

  return (
    <div
      className={`emp-toast ${notice.type}`}
      role={notice.type === "error" || notice.type === "warning" ? "alert" : "status"}
      aria-live={notice.type === "error" || notice.type === "warning" ? "assertive" : "polite"}
    >
      <div className="emp-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="emp-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="emp-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function ImagePreviewModal({ show, imageUrl, onHide }) {
  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdropClassName="emp-modal-backdrop">
      <Modal.Body className="emp-image-modal">
        {imageUrl ? <img src={imageUrl} alt="preview" /> : null}
      </Modal.Body>
    </Modal>
  );
}

export default function Employees() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [employees, setEmployees] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [salary, setSalary] = useState("");
  const [shiftType, setShiftType] = useState("morning");
  const [doj, setDoj] = useState("");
  const [salaryCreditDay, setSalaryCreditDay] = useState("1st");
  const [photo, setPhoto] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const recordsPerPage = 5;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    showNotice(
      "warning",
      "Create user first",
      "Please create the user first in the Users screen before adding employee details."
    );
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === "warning" || notice.type === "error" ? 5200 : 3600
    );
    return () => clearTimeout(timer);
  }, [notice]);

  const showNotice = (type, title, message) => {
    setNotice({
      id: Date.now(),
      message,
      title,
      type,
    });
  };

  const fetchEmployees = async () => {
    const res = await api.get("/api/employees", { headers });
    setEmployees(res.data || []);
  };

  const fetchOperators = async () => {
    const res = await api.get("/api/users?scope=tenant", { headers });
    const ops = (res.data || []).filter((u) => u.role === "operator" || u.role === "manager");
    setOperators(ops);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchEmployees(), fetchOperators()]);
    } catch (err) {
      console.error("Employees load error:", err);
      showNotice(
        "error",
        "Load failed",
        err?.response?.data?.error || "Unable to load employees right now."
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingEmployeeId(null);
    setSelectedUser("");
    setSalary("");
    setShiftType("morning");
    setDoj("");
    setSalaryCreditDay("1st");
    setPhoto(null);
    setDocumentFile(null);
  };

  const getOperatorName = (userId) => {
    const user = operators.find((op) => String(op.id) === String(userId));
    return user ? user.name : `User ${userId}`;
  };

  const openEdit = (employee) => {
    setEditingEmployeeId(employee.id);
    setSelectedUser(String(employee.user_id || ""));
    setSalary(String(employee.salary || ""));
    setShiftType(employee.shift_type || "morning");
    setDoj(employee.date_of_joining || "");
    setSalaryCreditDay(employee.salary_credit_day || "1st");
    setPhoto(null);
    setDocumentFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openImageModal = (imageUrl) => {
    setModalImage(imageUrl);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalImage(null);
  };

  const validateForm = () => {
    if (!selectedUser) {
      showNotice("warning", "Staff member required", "Please select an operator or manager first.");
      return false;
    }

    if (!salary || Number(salary) <= 0) {
      showNotice("warning", "Salary required", "Please enter a valid salary amount.");
      return false;
    }

    if (!doj) {
      showNotice("warning", "Joining date required", "Please select the date of joining.");
      return false;
    }

    return true;
  };

  const saveEmployee = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("user_id", selectedUser);
      formData.append("salary", parseFloat(salary));
      formData.append("shift_type", shiftType);
      formData.append("date_of_joining", doj);
      formData.append("salary_credit_day", salaryCreditDay);
      if (photo) formData.append("photo", photo);
      if (documentFile) formData.append("document", documentFile);

      const method = editingEmployeeId ? "put" : "post";
      const url = editingEmployeeId ? `/api/employees/${editingEmployeeId}` : "/api/employees";

      await api[method](url, formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      });

      showNotice(
        "success",
        editingEmployeeId ? "Updated" : "Saved",
        editingEmployeeId ? "Employee record updated successfully." : "Employee record saved successfully."
      );
      resetForm();
      await loadData();
    } catch (err) {
      console.error("Employees save error:", err);
      showNotice(
        "error",
        "Save failed",
        err?.response?.data?.error || "Unable to save employee right now."
      );
    } finally {
      setSaving(false);
    }
  };

  const filteredEmployees = employees.filter((emp) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;

    const name = getOperatorName(emp.user_id).toLowerCase();
    const shift = String(emp.shift_type || "").toLowerCase();
    const creditDay = String(emp.salary_credit_day || "").toLowerCase();
    const dojText = String(emp.date_of_joining || "").toLowerCase();
    return (
      name.includes(q) ||
      shift.includes(q) ||
      creditDay.includes(q) ||
      dojText.includes(q) ||
      String(emp.salary || "").includes(q)
    );
  });

  const totalPages = Math.max(Math.ceil(filteredEmployees.length / recordsPerPage), 1);
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return filteredEmployees.slice(start, start + recordsPerPage);
  }, [currentPage, filteredEmployees]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalSalary = useMemo(
    () => employees.reduce((sum, emp) => sum + Number(emp.salary || 0), 0),
    [employees]
  );

  const summaryCards = [
    {
      label: "Employees",
      value: employees.length,
      helper: "Profiles on file",
      tone: "blue",
      icon: FaUsers,
    },
    {
      label: "Operators",
      value: operators.length,
      helper: "Available staff users",
      tone: "green",
      icon: FaWallet,
    },
    {
      label: "Monthly Salary Pool",
      value: totalSalary,
      helper: "Combined salary baseline",
      tone: "amber",
      icon: FaClock,
      isMoney: true,
    },
    {
      label: "Visible Rows",
      value: filteredEmployees.length,
      helper: "Matches current search",
      tone: "slate",
      icon: FaFileImage,
    },
  ];

  return (
    <div className="emp-page text-light">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="emp-hero">
        <div>
          <div className="emp-eyebrow">Workforce Control</div>
          <h2>Employee management</h2>
          <p>
            Keep operator profiles, salary setup, shift allocation, and document previews in one
            clean station-friendly workspace.
          </p>
          <div className="emp-hero-meta">
            <span>
              <FaUsers aria-hidden="true" />
              {employees.length} employees
            </span>
            <span>
              <FaClock aria-hidden="true" />
              {operators.length} operators available
            </span>
          </div>
        </div>

        <div className="emp-hero-actions">
          <button className="btn emp-secondary-action" type="button" onClick={loadData}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button className="btn emp-secondary-action" type="button" onClick={resetForm}>
            <FaTimes aria-hidden="true" />
            Reset
          </button>
          <button className="btn emp-primary-action" type="button" onClick={() => navigate("/admin/users")}>
            <FaPlus aria-hidden="true" />
            Create User
          </button>
        </div>
      </section>

      <section className="emp-summary-grid">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <article className={`emp-summary-card ${card.tone}`} key={card.label}>
              <div className="emp-summary-icon">
                <Icon aria-hidden="true" />
              </div>
              <div className="emp-summary-copy">
                <span>{card.label}</span>
                <strong>{card.isMoney ? `₹${card.value.toFixed(2)}` : card.value}</strong>
                <em>{card.helper}</em>
              </div>
            </article>
          );
        })}
      </section>

      <section className="card-like emp-form-card">
          <div className="emp-card-head">
            <div>
              <span>Profile Entry</span>
              <h3>{editingEmployeeId ? "Edit employee" : "Add a new employee"}</h3>
            </div>
            <button type="button" className="btn btn-sm btn-light emp-reset-btn" onClick={resetForm}>
              {editingEmployeeId ? "Cancel Edit" : "Clear"}
            </button>
          </div>

          <div className="emp-setup-banner">
            <div>
              <strong>Users first, then employee details</strong>
              <span>
                The employee record must be linked to an existing user account. Use the Users screen
                to create the login first.
              </span>
            </div>
            <button type="button" className="btn emp-secondary-action" onClick={() => navigate("/admin/users")}>
              Go to Users
            </button>
          </div>

        <div className="row g-3">
          <div className="col-lg-4 col-md-6">
            <label className="form-label">Select Operator</label>
            <select
              className="form-select"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="">Select Operator / Manager</option>
              {operators.map((op) => (
                <option key={op.id} value={op.id}>
                  {op.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-2 col-md-6">
            <label className="form-label">Salary</label>
            <input
              className="form-control"
              type="number"
              value={salary}
              onChange={(e) => setSalary(e.target.value)}
              placeholder="Amount"
            />
          </div>

          <div className="col-lg-2 col-md-6">
            <label className="form-label">Shift Type</label>
            <select
              className="form-select"
              value={shiftType}
              onChange={(e) => setShiftType(e.target.value)}
            >
              <option value="morning">Morning</option>
              <option value="night">Night</option>
            </select>
          </div>

          <div className="col-lg-2 col-md-6">
            <label className="form-label">DOJ</label>
            <input
              type="date"
              className="form-control"
              value={doj}
              onChange={(e) => setDoj(e.target.value)}
            />
          </div>

          <div className="col-lg-2 col-md-6">
            <label className="form-label">Salary Credit Day</label>
            <select
              className="form-select"
              value={salaryCreditDay}
              onChange={(e) => setSalaryCreditDay(e.target.value)}
            >
              {salaryDayOptions.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-3 col-md-6">
            <label className="form-label">Photo</label>
            <input
              type="file"
              className="form-control file-input"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label className="form-label">Document</label>
            <input
              type="file"
              className="form-control file-input"
              onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="col-lg-2 col-md-6 d-grid align-items-end">
            <button className="btn btn-success emp-save-btn" onClick={saveEmployee} disabled={saving}>
              <FaSave aria-hidden="true" />
              {saving ? "Saving..." : editingEmployeeId ? "Update Employee" : "Save Employee"}
            </button>
          </div>

          <div className="col-lg-4 col-md-6">
            <label className="form-label">Search</label>
            <div className="emp-search-shell">
              <FaSearch aria-hidden="true" />
              <input
                className="form-control"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, shift, salary or date"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="card-like emp-table-card">
        <div className="emp-card-head">
          <div>
            <span>Employee List</span>
            <h3>Profiles and document previews</h3>
          </div>
          <div className="emp-table-meta">
            <span>{filteredEmployees.length} rows</span>
            <strong>{loading ? "Loading..." : `${currentPage}/${totalPages}`}</strong>
          </div>
        </div>

        <div className="table-responsive emp-table-wrap">
          <table className="table emp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Salary</th>
                <th>Shift</th>
                <th>DOJ</th>
                <th>Credit Day</th>
                <th>Photo</th>
                <th>Document</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((emp) => {
                const photoUrl = emp.photo ? `${BACKEND_BASE_URL}/${emp.photo}` : "";
                const docUrl = emp.document_proof ? `${BACKEND_BASE_URL}/${emp.document_proof}` : "";

                return (
                  <tr key={emp.id}>
                    <td data-label="Name">{getOperatorName(emp.user_id)}</td>
                    <td data-label="Salary">₹{Number(emp.salary || 0).toFixed(2)}</td>
                    <td data-label="Shift">
                      <span className={`emp-shift-pill ${String(emp.shift_type || "").toLowerCase()}`}>
                        {emp.shift_type}
                      </span>
                    </td>
                    <td data-label="DOJ">{emp.date_of_joining}</td>
                    <td data-label="Credit Day">{emp.salary_credit_day}</td>
                    <td data-label="Photo">
                      {photoUrl ? (
                        <button
                          type="button"
                          className="emp-thumb-btn"
                          onClick={() => openImageModal(photoUrl)}
                        >
                          <img src={photoUrl} alt="Employee photo" />
                        </button>
                      ) : (
                        <span className="emp-empty-cell">No photo</span>
                      )}
                    </td>
                    <td data-label="Document">
                      {docUrl ? (
                        <button
                          type="button"
                          className="emp-thumb-btn"
                          onClick={() => openImageModal(docUrl)}
                        >
                          <img src={docUrl} alt="Document proof" />
                        </button>
                      ) : (
                        <span className="emp-empty-cell">No document</span>
                      )}
                    </td>
                    <td data-label="Actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-warning"
                        onClick={() => openEdit(emp)}
                      >
                        <FaEdit aria-hidden="true" />
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}

              {!currentData.length && (
                <tr>
                  <td colSpan="8">
                    <div className="emp-empty-table">
                      <FaUsers aria-hidden="true" />
                      <strong>No employees found</strong>
                      <span>Try a different search or add a new employee profile.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredEmployees.length > 0 && (
          <div className="emp-pagination-bar">
            <button
              type="button"
              className="emp-page-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>

            <div className="emp-page-list">
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`emp-page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="emp-page-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      <ImagePreviewModal show={showModal} imageUrl={modalImage} onHide={closeModal} />
    </div>
  );
}
